export const STORE_TAX_COUNT = 3;
export const FISCAL_TYPES = ["F", "P", "S"];

const FEDERAL_NAMES = ["tps", "gst"];
const PROVINCIAL_NAMES = ["tvq", "qst", "pst"];

export const taxIdAt = (tax, index) => tax?.id || `tax${index + 1}`;

export const fiscalTypeOf = (tax) => {
  const explicit = String(tax?.fiscalType || "").toUpperCase();
  if (FISCAL_TYPES.includes(explicit)) return explicit;
  const name = String(tax?.taxName || "").trim().toLowerCase();
  if (FEDERAL_NAMES.includes(name)) return "F";
  if (PROVINCIAL_NAMES.includes(name)) return "P";
  return "S";
};

let platformTaxes = null;
let platformOverride = { allowStoreOverride: false, allowTaxOverride: false };
const overrideListeners = new Set();

export const setPlatformTaxes = (taxes, config) => {
  platformTaxes = Array.isArray(taxes) ? taxes : null;
  platformOverride = {
    allowStoreOverride: config?.allowAllStoreOverride === true,
    allowTaxOverride: config?.allowAllStoreTaxOverride === true,
  };
  overrideListeners.forEach((listener) => listener(platformOverride));
};

export const subscribePlatformOverride = (listener) => {
  overrideListeners.add(listener);
  listener(platformOverride);
  return () => overrideListeners.delete(listener);
};

export const mergePlatformTaxes = (storeTaxes, source = platformTaxes, options = {}) => {
  const local = Array.isArray(storeTaxes) ? storeTaxes : [];
  if (!Array.isArray(source) || source.length === 0) return local;
  const allowStoreOverride = options.allowStoreOverride === true;
  const allowTaxOverride = options.allowTaxOverride === true;
  return source.slice(0, STORE_TAX_COUNT).map((platform, index) => {
    const id = taxIdAt(platform, index);
    const match = local.find((tax, itemIndex) => taxIdAt(tax, itemIndex) === id) || {};
    const override = match.exemptionOverride;
    const overridden = allowStoreOverride && Boolean(override && typeof override === "object");
    return {
      id,
      taxName: allowTaxOverride && String(match.taxName || "").trim() ? String(match.taxName).trim() : String(platform.taxName || "").trim(),
      taxValue: allowTaxOverride && match.taxValue != null ? (Number(match.taxValue) || 0) : (Number(platform.taxValue) || 0),
      fiscalType: allowTaxOverride && match.fiscalType ? fiscalTypeOf(match) : fiscalTypeOf(platform),
      isSpecific: match.isSpecific === true,
      checkedItems: Array.isArray(match.checkedItems) ? match.checkedItems : [],
      standardExemption: exemptionOf(platform),
      exemption: overridden ? exemptionOf({ exemption: override }) : exemptionOf(platform),
      exemptionOverride: overridden ? exemptionOf({ exemption: override }) : null,
      exemptionOverridden: overridden,
    };
  });
};

export const storeTaxList = (store) => {
  const raw = Array.isArray(store?.taxes) ? store.taxes : (Array.isArray(store) ? store : []);
  return mergePlatformTaxes(raw, platformTaxes, platformOverride).slice(0, STORE_TAX_COUNT).map((tax, index) => ({
    ...tax,
    id: taxIdAt(tax, index),
    fiscalType: fiscalTypeOf(tax),
  }));
};

export const namedStoreTaxes = (store) => storeTaxList(store).filter((tax) => String(tax.taxName || "").trim());

export const defaultItemTaxIds = (store) => namedStoreTaxes(store)
  .filter((tax) => tax.fiscalType === "F" || tax.fiscalType === "P")
  .map((tax) => tax.id);

const lineKeys = (line) => [line?.id, line?._id, line?.sku].filter(Boolean);

const legacyApplies = (tax, line) => {
  if (!tax?.isSpecific) return true;
  const keys = lineKeys(line);
  return (tax.checkedItems || []).some((entry) => keys.includes(entry?.id));
};

export const hasItemTaxIds = (line) => Array.isArray(line?.taxIds);

export const lineTaxIds = (line, store) => {
  const taxes = storeTaxList(store);
  if (hasItemTaxIds(line)) {
    const known = new Set(taxes.map((tax) => tax.id));
    return line.taxIds.filter((id) => known.has(id));
  }
  return taxes.filter((tax) => legacyApplies(tax, line)).map((tax) => tax.id);
};

export const storeLocalDate = (at, timeZone) => {
  const date = at instanceof Date ? at : new Date(at ?? Date.now());
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
  }
};

export const exemptionOf = (tax) => {
  const rule = tax?.exemption && typeof tax.exemption === "object" ? tax.exemption : null;
  if (!rule) {
    const from = String(tax?.exemptFrom || "");
    const to = String(tax?.exemptTo || "");
    return { enabled: Boolean(from || to), from, to, rate: 0 };
  }
  return {
    enabled: rule.enabled === true,
    from: String(rule.from || ""),
    to: String(rule.to || ""),
    rate: Math.max(0, Number(rule.rate) || 0),
  };
};

export const storeLocalDateTime = (at, timeZone) => {
  const date = at instanceof Date ? at : new Date(at ?? Date.now());
  const options = { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-CA", { ...options, timeZone: timeZone || undefined }).formatToParts(date);
  } catch {
    parts = new Intl.DateTimeFormat("en-CA", options).formatToParts(date);
  }
  const part = (type) => parts.find((entry) => entry.type === type)?.value || "00";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
};

const ruleStart = (value) => (value && !value.includes("T") ? `${value}T00:00` : value);
const ruleEnd = (value) => (value && !value.includes("T") ? `${value}T23:59` : value);

export const exemptionStatus = (tax, at, timeZone) => {
  const { enabled, from, to } = exemptionOf(tax);
  if (!enabled) return "off";
  const now = storeLocalDateTime(at, timeZone);
  if (from && now < ruleStart(from)) return "upcoming";
  if (to && now > ruleEnd(to)) return "ended";
  return "active";
};

export const isExempt = (tax, at, timeZone) => exemptionStatus(tax, at, timeZone) === "active";

export const effectiveTaxRate = (tax, at, timeZone) => (
  isExempt(tax, at, timeZone) ? exemptionOf(tax).rate : Number(tax?.taxValue) || 0
);

const removedByExemption = (tax, at, timeZone) => isExempt(tax, at, timeZone) && exemptionOf(tax).rate === 0;

export const lineTaxCode = (line, store, at) => {
  const timeZone = store?.timeZone;
  const applied = new Set(lineTaxIds(line, store));
  const letters = new Set(
    namedStoreTaxes(store)
      .filter((tax) => applied.has(tax.id) && !removedByExemption(tax, at, timeZone))
      .map((tax) => tax.fiscalType)
  );
  const code = FISCAL_TYPES.filter((letter) => letters.has(letter)).join("");
  return code || "NON";
};

const roundCents = (value) => Math.round(value * 100) / 100;

const defaultLineAmount = (line) => Number(line?.price || 0) * Number(line?.amount ?? 1);

export const computeOrderTaxes = (lines, store, options = {}) => {
  const {
    at,
    lineAmount = defaultLineAmount,
    deliveryFee = 0,
    deliveryFeeDisableTax = false,
    round = roundCents,
  } = options;
  const timeZone = store?.timeZone;
  const rows = Array.isArray(lines) ? lines : [];
  const fee = Number(deliveryFee) || 0;
  return storeTaxList(store).map((tax) => {
    const itemsBase = rows.reduce((sum, line) => (
      lineTaxIds(line, store).includes(tax.id) ? sum + (Number(lineAmount(line)) || 0) : sum
    ), 0);
    const taxableBase = itemsBase + (deliveryFeeDisableTax || tax.fiscalType === "S" ? 0 : fee);
    const rate = effectiveTaxRate(tax, at, timeZone);
    return {
      taxId: tax.id,
      taxName: tax.taxName,
      taxValue: rate,
      taxAmount: round(taxableBase * (rate / 100)),
      fiscalType: tax.fiscalType,
    };
  });
};

export const withLineTaxes = (lines, store, options = {}) => {
  const { at, lineAmount = defaultLineAmount } = options;
  const timeZone = store?.timeZone;
  const taxes = namedStoreTaxes(store);
  return (Array.isArray(lines) ? lines : []).map((line) => {
    const applied = new Set(lineTaxIds(line, store));
    const amount = Number(lineAmount(line)) || 0;
    const lineTaxes = {};
    taxes.forEach((tax) => {
      if (applied.has(tax.id)) lineTaxes[tax.id] = roundCents(amount * effectiveTaxRate(tax, at, timeZone) / 100);
    });
    return { ...line, lineTaxes, tax: lineTaxCode(line, store, at) };
  });
};

export const refundFromLines = (order, refundLines) => {
  const lines = Array.isArray(refundLines) ? refundLines : [];
  if (lines.length === 0 || !lines.every((line) => line?.lineTaxes && typeof line.lineTaxes === "object")) return null;
  const types = new Map((order?.taxes || [])
    .filter((tax) => tax?.taxId)
    .map((tax) => [tax.taxId, fiscalTypeOf(tax)]));
  let tps = 0;
  let tvq = 0;
  let total = 0;
  lines.forEach((line) => {
    const refunded = Number(line.qty ?? line.quantity ?? line.amount) || 0;
    const sold = Number(line.amount ?? line.quantity) || refunded || 1;
    const share = refunded / sold;
    Object.entries(line.lineTaxes).forEach(([id, value]) => {
      const part = (Number(value) || 0) * share;
      total += part;
      const type = types.get(id);
      if (type === "F") tps += part;
      else if (type === "P") tvq += part;
    });
  });
  return { tps: roundCents(tps), tvq: roundCents(tvq), taxTotal: roundCents(total) };
};

export const withSpecificListEntry = (tax, itemId, applies) => {
  const current = (tax?.checkedItems || []).filter((entry) => entry?.id);
  const others = current.filter((entry) => entry.id !== itemId).map((entry) => ({ id: entry.id }));
  return {
    ...tax,
    isSpecific: true,
    checkedItems: applies ? [...others, { id: itemId }] : others,
  };
};
